import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Play,
  FlaskConical,
  CheckCircle2,
  Lock as LockIcon,
  XCircle,
  Sparkles,
  ShieldQuestion,
  MapPin,
  Tag,
  Image as ImageIcon,
} from 'lucide-react';
import { usePlant } from '../../state/PlantContext';
import { useAuth } from '../../state/AuthContext';
import { permitApi, conflictApi, ApiError } from '../../lib/api';
import type { Conflict, EvaluationHistoryItem, OverrideRecord, PermitDetail, EntityExtractionResult } from '../../lib/types';
import { ResultBadge, PermitStatusBadge } from '../../components/StatusBadge';
import { RuleChip } from '../../components/RuleChip';
import { Spinner, ErrorBanner, DisclosureBanner } from '../../components/Misc';
import { Modal } from '../../components/Modal';
import { formatDateTime, titleCase } from '../../lib/format';

const CO_AUTH_ROLES = ['area_authority', 'safety_officer', 'platform_admin'];
const EVALUATE_ROLES = ['org_admin', 'safety_officer', 'area_authority', 'platform_admin'];
const ISSUE_ROLES = ['org_admin', 'area_authority', 'platform_admin'];
const CLOSE_ROLES = ['org_admin', 'area_authority', 'safety_officer', 'platform_admin'];
const GAS_TEST_ROLES = ['safety_officer', 'area_authority', 'org_admin', 'platform_admin'];

export default function PermitDetailPage() {
  const { permitId } = useParams<{ permitId: string }>();
  const { currentPlant } = usePlant();
  const { user } = useAuth();

  const [permit, setPermit] = useState<PermitDetail | null>(null);
  const [evaluation, setEvaluation] = useState<EvaluationHistoryItem | null>(null);
  const [overrides, setOverrides] = useState<OverrideRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const [gasTestOpen, setGasTestOpen] = useState(false);
  const [overrideConflict, setOverrideConflict] = useState<Conflict | null>(null);
  const [explanations, setExplanations] = useState<Record<string, { text: string; source: string } | 'loading' | 'error'>>({});
  const [extraction, setExtraction] = useState<EntityExtractionResult | null>(null);
  const [extracting, setExtracting] = useState(false);

  const load = useCallback(async () => {
    if (!currentPlant || !permitId) return;
    setError(null);
    try {
      const [detail, overridesRes] = await Promise.all([permitApi.get(currentPlant.id, permitId), conflictApi.listOverrides(currentPlant.id)]);
      setPermit(detail.permit);
      setEvaluation(detail.latestEvaluation);
      setOverrides(overridesRes.overrides.filter((o) => o.permitNumber === detail.permit.permitNumber));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load this permit.');
    } finally {
      setLoading(false);
    }
  }, [currentPlant, permitId]);

  useEffect(() => {
    setLoading(true);
    void load();
  }, [load]);

  if (!currentPlant || loading) return <Spinner label="Loading permit…" />;
  if (error && !permit) return <ErrorBanner message={error} />;
  if (!permit) return null;

  const overrideForConflict = (conflictId: string) => overrides.find((o) => o.conflictId === conflictId);

  async function runAction<T>(key: string, fn: () => Promise<T>) {
    setBusy(key);
    setError(null);
    try {
      await fn();
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Something went wrong.');
    } finally {
      setBusy(null);
    }
  }

  const canEvaluate = user && EVALUATE_ROLES.includes(user.role);
  const canIssue = user && ISSUE_ROLES.includes(user.role) && ['SUBMITTED', 'UNDER_REVIEW', 'APPROVED'].includes(permit.status);
  const canClose = user && CLOSE_ROLES.includes(user.role) && !['CLOSED', 'CANCELLED'].includes(permit.status);
  const canRecordGasTest = user && GAS_TEST_ROLES.includes(user.role);

  async function loadExplanation(conflict: Conflict) {
    if (!currentPlant || !permitId) return;
    setExplanations((s) => ({ ...s, [conflict.id]: 'loading' }));
    try {
      const res = await permitApi.explain(currentPlant.id, permitId, conflict.id);
      setExplanations((s) => ({ ...s, [conflict.id]: res }));
    } catch {
      setExplanations((s) => ({ ...s, [conflict.id]: 'error' }));
    }
  }

  async function handleExtractTags() {
    if (!currentPlant || !permitId) return;
    setExtracting(true);
    try {
      const res = await permitApi.extractTags(currentPlant.id, permitId);
      setExtraction(res);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'AI extraction is unavailable right now.');
    } finally {
      setExtracting(false);
    }
  }

  async function confirmSuggestedTag(nodeId: string, confidence: number) {
    if (!currentPlant || !permitId) return;
    await runAction(`confirm-tag-${nodeId}`, () => permitApi.confirmTags(currentPlant.id, permitId, [{ nodeId, source: 'AI_EXTRACTED', confidence }]));
    setExtraction((prev) => (prev ? { ...prev, tags: prev.tags.filter((t) => t.resolvedNodeId !== nodeId) } : prev));
  }

  return (
    <div className="stack gap-5">
      <Link to="/app/permits" className="row gap-1 body-small text-tertiary" style={{ width: 'fit-content' }}>
        <ArrowLeft size={14} /> Back to permits
      </Link>

      <div className="row" style={{ alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
        <div className="stack gap-2">
          <div className="row gap-3">
            <h1 className="h2-section mono">{permit.permitNumber}</h1>
            <PermitStatusBadge status={permit.status} />
          </div>
          <p className="body-text" style={{ maxWidth: 640 }}>{permit.scopeText}</p>
          <div className="row gap-4" style={{ flexWrap: 'wrap' }}>
            <MetaItem icon={<Tag size={14} />} label={titleCase(permit.permitType)} />
            {permit.workLocation && <MetaItem icon={<MapPin size={14} />} label={permit.workLocation.name} />}
          </div>
        </div>
        <div className="spacer" />
        <ResultBadge result={evaluation?.overallResult} />
      </div>

      {error && <ErrorBanner message={error} />}

      <div className="row gap-2" style={{ flexWrap: 'wrap' }}>
        {canRecordGasTest && (
          <button className="btn btn-secondary" onClick={() => setGasTestOpen(true)}>
            <FlaskConical size={16} /> Record gas test
          </button>
        )}
        {canEvaluate && (
          <button className="btn btn-primary" disabled={busy === 'evaluate'} onClick={() => runAction('evaluate', () => permitApi.evaluate(currentPlant.id, permit.id))}>
            <Play size={16} /> {busy === 'evaluate' ? 'Evaluating…' : 'Run conflict evaluation'}
          </button>
        )}
        {canIssue && (
          <button className="btn btn-primary" disabled={busy === 'issue'} onClick={() => runAction('issue', () => permitApi.issue(currentPlant.id, permit.id))}>
            <CheckCircle2 size={16} /> {busy === 'issue' ? 'Issuing…' : 'Issue permit'}
          </button>
        )}
        {canClose && (
          <button className="btn btn-ghost" disabled={busy === 'close'} onClick={() => runAction('close', () => permitApi.close(currentPlant.id, permit.id))}>
            <XCircle size={16} /> {busy === 'close' ? 'Closing…' : 'Close permit'}
          </button>
        )}
      </div>

      {/* Gas test */}
      <div className="card stack gap-2">
        <div className="h3" style={{ fontSize: '1.0625rem' }}>Latest gas test</div>
        {permit.latestGasTest ? (
          <div className="grid-4">
            <MiniStat label="Result" value={permit.latestGasTest.result} tone={permit.latestGasTest.result === 'PASS' ? 'good' : 'bad'} />
            <MiniStat label="O₂ %" value={permit.latestGasTest.o2Pct?.toString() ?? '—'} />
            <MiniStat label="LEL %" value={permit.latestGasTest.lelPct?.toString() ?? '—'} />
            <MiniStat label="Expires" value={formatDateTime(permit.latestGasTest.expiresAt)} />
          </div>
        ) : (
          <p className="body-small text-tertiary">No gas test recorded yet{['HOT_WORK', 'CONFINED_SPACE', 'LINE_BREAKING'].includes(permit.permitType) ? ' — required for this permit type before it can clear evaluation.' : '.'}</p>
        )}
      </div>

      {/* AI tag extraction */}
      <div className="card stack gap-3">
        <div className="row gap-2">
          <Sparkles size={18} color="var(--accent)" />
          <div className="h3" style={{ fontSize: '1.0625rem' }}>AI-assisted equipment tagging</div>
          <div className="spacer" />
          <button className="btn btn-secondary btn-sm" onClick={handleExtractTags} disabled={extracting}>
            {extracting ? 'Analyzing scope text…' : 'Suggest equipment tags with AI'}
          </button>
        </div>
        <DisclosureBanner>
          <strong>AI-assisted, not AI-decided.</strong> This parses the scope text for equipment
          references only. Nothing is added to the permit — and nothing reaches the conflict engine —
          until you confirm a suggestion below.
        </DisclosureBanner>
        {extraction && (
          extraction.tags.length > 0 ? (
            <div className="stack gap-2">
              {extraction.tags.map((t) => (
                <div key={t.rawText} className="row gap-3" style={{ padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                  <span className="mono body-small">"{t.rawText}"</span>
                  <span className="body-small text-tertiary">→</span>
                  <span className="mono body-small" style={{ fontWeight: 600 }}>{t.resolvedTagNumber ?? 'no registry match'}</span>
                  <span className="pill">{Math.round(t.extractionConfidence * 100)}% confidence</span>
                  <div className="spacer" />
                  {t.resolvedNodeId && (
                    <button className="btn btn-secondary btn-sm" onClick={() => confirmSuggestedTag(t.resolvedNodeId!, t.extractionConfidence)} disabled={busy === `confirm-tag-${t.resolvedNodeId}`}>
                      Confirm tag
                    </button>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="body-small text-tertiary">{extraction.source === 'UNAVAILABLE' ? (extraction.reason ?? 'The AI extraction service is not configured on this deployment.') : 'No candidate equipment references found in the scope text.'}</p>
          )
        )}
      </div>

      {/* Evaluation results */}
      <div className="stack gap-3">
        <div className="row">
          <div className="h3">Conflict evaluation</div>
          <div className="spacer" />
          {evaluation && <span className="body-small text-tertiary">Last run {formatDateTime(evaluation.evaluatedAt)}{evaluation.durationMs !== null ? ` · ${evaluation.durationMs}ms` : ''}</span>}
        </div>

        {!evaluation ? (
          <div className="card">
            <p className="body-small text-tertiary">No evaluation has been run yet. Run the conflict evaluation to check this permit against every spatial, isolation, and temporal safety rule.</p>
          </div>
        ) : evaluation.conflicts.length === 0 ? (
          <div className="card row gap-2" style={{ borderColor: 'rgba(21,127,60,0.3)', background: 'var(--status-clear-bg)' }}>
            <CheckCircle2 size={20} color="var(--status-clear)" />
            <span className="body-small" style={{ color: 'var(--status-clear)', fontWeight: 600 }}>No conflicts found. This permit is clear to issue.</span>
          </div>
        ) : (
          <div className="stack gap-3">
            {evaluation.conflicts.map((conflict) => {
              const explanation = explanations[conflict.id];
              const override = overrideForConflict(conflict.id);
              return (
                <div key={conflict.id} className="card stack gap-3" style={{ borderColor: conflict.severity === 'CRITICAL_STOP_WORK' ? 'rgba(179,38,30,0.3)' : 'rgba(176,106,0,0.3)' }}>
                  <div className="row gap-2" style={{ flexWrap: 'wrap' }}>
                    <span className={`status-badge ${conflict.severity === 'CRITICAL_STOP_WORK' ? 'critical' : 'advisory'}`}>{conflict.severity.replace(/_/g, ' ')}</span>
                    <RuleChip code={conflict.ruleCode} />
                    <div className="spacer" />
                    <span className="pill">{conflict.status}</span>
                  </div>
                  <div className="h3" style={{ fontSize: '1.0625rem' }}>{conflict.title}</div>
                  <p className="body-text">{conflict.explanationText}</p>
                  {conflict.distanceM !== null && <p className="body-small text-tertiary">Measured distance: <span className="mono">{conflict.distanceM.toFixed(2)} m</span></p>}
                  {conflict.requiredMitigation && (
                    <div className="body-small" style={{ background: 'var(--surface-alt)', borderRadius: 8, padding: '10px 12px' }}>
                      <strong>Required mitigation:</strong> {conflict.requiredMitigation}
                    </div>
                  )}

                  <div className="row gap-2" style={{ flexWrap: 'wrap' }}>
                    <button className="btn btn-ghost btn-sm" onClick={() => loadExplanation(conflict)} disabled={explanation === 'loading'}>
                      <Sparkles size={14} /> {explanation === 'loading' ? 'Generating…' : 'Explain in plain language (AI)'}
                    </button>
                    {conflict.severity === 'CRITICAL_STOP_WORK' && conflict.status !== 'OVERRIDDEN' && !override && (
                      <button className="btn btn-secondary btn-sm" onClick={() => setOverrideConflict(conflict)}>
                        <ShieldQuestion size={14} /> Request override
                      </button>
                    )}
                  </div>

                  {explanation && explanation !== 'loading' && explanation !== 'error' && (
                    <div className="disclosure-banner stack gap-1" style={{ alignItems: 'flex-start' }}>
                      <span className="pill" style={{ background: 'rgba(255,255,255,0.6)' }}>{explanation.source === 'AI_GENERATED' ? 'AI-generated · non-authoritative' : 'Deterministic template'}</span>
                      <p style={{ margin: 0 }}>{explanation.text}</p>
                    </div>
                  )}
                  {explanation === 'error' && <ErrorBanner message="Could not generate an explanation right now." />}

                  {override && <OverrideStatusRow override={override} plantId={currentPlant.id} onChanged={load} currentUserId={user?.id} canCoAuthorize={Boolean(user && CO_AUTH_ROLES.includes(user.role))} />}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {gasTestOpen && <GasTestModal onClose={() => setGasTestOpen(false)} onSubmit={async (input) => { await runAction('gastest', () => permitApi.recordGasTest(currentPlant.id, permit.id, input)); setGasTestOpen(false); }} />}
      {overrideConflict && (
        <OverrideModal
          conflict={overrideConflict}
          onClose={() => setOverrideConflict(null)}
          onSubmit={async (input) => {
            await runAction('override', () => conflictApi.requestOverride(currentPlant.id, overrideConflict.id, input));
            setOverrideConflict(null);
          }}
        />
      )}
    </div>
  );
}

function MetaItem({ icon, label }: { icon: ReactNode; label: string }) {
  return (
    <span className="row gap-1 body-small text-tertiary">
      {icon} {label}
    </span>
  );
}

function MiniStat({ label, value, tone }: { label: string; value: string; tone?: 'good' | 'bad' }) {
  return (
    <div className="stack gap-1">
      <span className="body-small text-tertiary">{label}</span>
      <span className="mono" style={{ fontWeight: 700, color: tone === 'good' ? 'var(--status-clear)' : tone === 'bad' ? 'var(--status-critical)' : 'var(--text)' }}>{value}</span>
    </div>
  );
}

function OverrideStatusRow({ override, canCoAuthorize, onChanged, plantId, currentUserId }: { override: OverrideRecord; canCoAuthorize: boolean; onChanged: () => Promise<void>; plantId: string; currentUserId?: string }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const isSameUser = currentUserId && currentUserId === override.requestedBy;

  async function decide(approve: boolean) {
    setBusy(true);
    setErr(null);
    try {
      await conflictApi.coAuthorize(plantId, override.id, approve);
      await onChanged();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Could not record decision.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="stack gap-2" style={{ background: 'var(--surface-sunken)', borderRadius: 10, padding: 14 }}>
      <div className="row gap-2">
        <LockIcon size={16} color="var(--status-indeterminate)" />
        <span className="body-small" style={{ fontWeight: 700 }}>Human override — {override.status.replace(/_/g, ' ')}</span>
      </div>
      <p className="body-small"><strong>Justification:</strong> {override.justificationText}</p>
      <p className="body-small"><strong>Mitigation control:</strong> {override.mitigationControl}</p>
      {override.evidencePhotoPath && (
        <a
          href={`/api/files/${override.evidencePhotoPath}`}
          target="_blank"
          rel="noreferrer"
          className="body-small row gap-1"
          style={{ alignItems: 'center', color: 'var(--accent)', fontWeight: 600, width: 'fit-content' }}
        >
          <ImageIcon size={14} /> View evidence photo
        </a>
      )}
      {err && <ErrorBanner message={err} />}
      {override.status === 'PENDING_CO_AUTHORIZATION' && canCoAuthorize && (
        <div className="row gap-2">
          <button className="btn btn-primary btn-sm" disabled={busy || Boolean(isSameUser)} onClick={() => decide(true)}>Co-authorize (approve)</button>
          <button className="btn btn-ghost btn-sm" disabled={busy} onClick={() => decide(false)}>Reject</button>
          {isSameUser && <span className="body-small text-tertiary">Segregation of duties: you requested this override, so you cannot co-authorize it.</span>}
        </div>
      )}
    </div>
  );
}

function GasTestModal({ onClose, onSubmit }: { onClose: () => void; onSubmit: (input: { o2Pct?: number; lelPct?: number; h2sPpm?: number; toxicPpm?: number; result: 'PASS' | 'FAIL'; validForMinutes?: number }) => Promise<void> }) {
  const [o2, setO2] = useState('20.9');
  const [lel, setLel] = useState('0');
  const [h2s, setH2s] = useState('0');
  const [toxic, setToxic] = useState('0');
  const [result, setResult] = useState<'PASS' | 'FAIL'>('PASS');
  const [validMinutes, setValidMinutes] = useState('30');
  const [submitting, setSubmitting] = useState(false);

  async function submit() {
    setSubmitting(true);
    try {
      await onSubmit({ o2Pct: Number(o2), lelPct: Number(lel), h2sPpm: Number(h2s), toxicPpm: Number(toxic), result, validForMinutes: Number(validMinutes) });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal title="Record gas test" onClose={onClose}>
      <div className="stack gap-3">
        <div className="grid-2">
          <div className="field"><label>O₂ %</label><input type="number" step="0.1" value={o2} onChange={(e) => setO2(e.target.value)} /></div>
          <div className="field"><label>LEL %</label><input type="number" step="0.1" value={lel} onChange={(e) => setLel(e.target.value)} /></div>
          <div className="field"><label>H₂S (ppm)</label><input type="number" step="0.1" value={h2s} onChange={(e) => setH2s(e.target.value)} /></div>
          <div className="field"><label>Toxic (ppm)</label><input type="number" step="0.1" value={toxic} onChange={(e) => setToxic(e.target.value)} /></div>
        </div>
        <div className="field">
          <label>Result</label>
          <select value={result} onChange={(e) => setResult(e.target.value as 'PASS' | 'FAIL')}>
            <option value="PASS">PASS</option>
            <option value="FAIL">FAIL</option>
          </select>
        </div>
        <div className="field"><label>Valid for (minutes)</label><input type="number" value={validMinutes} onChange={(e) => setValidMinutes(e.target.value)} /></div>
        <div className="row gap-2" style={{ marginTop: 8 }}>
          <button className="btn btn-primary" onClick={submit} disabled={submitting}>{submitting ? 'Saving…' : 'Save gas test'}</button>
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </Modal>
  );
}

function OverrideModal({ conflict, onClose, onSubmit }: { conflict: Conflict; onClose: () => void; onSubmit: (input: { justificationText: string; mitigationControl: string; evidencePhoto?: File | null }) => Promise<void> }) {
  const [justification, setJustification] = useState('');
  const [mitigation, setMitigation] = useState('');
  const [photo, setPhoto] = useState<File | null>(null);
  const [photoError, setPhotoError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const MAX_PHOTO_BYTES = 10 * 1024 * 1024;

  function onPhotoChange(file: File | null) {
    setPhotoError(null);
    if (file && !file.type.startsWith('image/')) {
      setPhotoError('Please choose an image file (JPEG, PNG, etc.).');
      setPhoto(null);
      return;
    }
    if (file && file.size > MAX_PHOTO_BYTES) {
      setPhotoError('Image is larger than the 10 MB limit.');
      setPhoto(null);
      return;
    }
    setPhoto(file);
  }

  async function submit() {
    setSubmitting(true);
    try {
      await onSubmit({ justificationText: justification, mitigationControl: mitigation, evidencePhoto: photo });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal title="Request override" onClose={onClose}>
      <div className="stack gap-3">
        <DisclosureBanner>
          Overriding <RuleChip code={conflict.ruleCode} /> requires dual sign-off: a second,
          independent safety authority must co-authorize before this permit can be issued.
        </DisclosureBanner>
        <div className="field">
          <label>Justification (min. 10 characters)</label>
          <textarea rows={3} value={justification} onChange={(e) => setJustification(e.target.value)} placeholder="Why should this proceed despite the finding above?" />
        </div>
        <div className="field">
          <label>Mitigation control (min. 5 characters)</label>
          <textarea rows={2} value={mitigation} onChange={(e) => setMitigation(e.target.value)} placeholder="What compensating control is in place?" />
        </div>
        <div className="field">
          <label>Evidence photo (optional)</label>
          <input
            type="file"
            accept="image/*"
            onChange={(e) => onPhotoChange(e.target.files?.[0] ?? null)}
          />
          <p className="body-small text-tertiary" style={{ marginTop: 4 }}>
            e.g. a photo of the compensating control actually in place (temporary barricade, fire
            watch standing by, spotter posted). Stored on the server's local persistent volume and
            attached to this override's audit-trail entry; never required to submit an override.
          </p>
          {photo && <p className="body-small" style={{ color: 'var(--status-clear)' }}>Selected: {photo.name} ({Math.round(photo.size / 1024)} KB)</p>}
          {photoError && <ErrorBanner message={photoError} />}
        </div>
        <div className="row gap-2" style={{ marginTop: 8 }}>
          <button className="btn btn-primary" disabled={submitting || justification.trim().length < 10 || mitigation.trim().length < 5} onClick={submit}>
            {submitting ? 'Submitting…' : 'Submit override request'}
          </button>
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </Modal>
  );
}
